import React, { useEffect, useState } from 'react';
import { useDevices, useEntries, useDeepEffect } from '../../utils/hooks';
import {
  VIEW_TABLE,
  VIEW_COMMAND,
  VIEW_DASHBOARD,
  VIEW_NOTIFICATIONS,
  VIEW_SIMULATION,
  ACCT_PLAN_LEVELS,
} from '../../utils/constants';
import __isEmpty from 'lodash/isEmpty';

import './WeatherDisplayMobile.scss';

import HeaderDefault from '../../components/HeaderDefault';
import FooterMobile from '../../components/FooterMobile';
import ViewChooser from '../../components/ViewChooser';
import Dashboard from '../../containers/WeatherDisplay/SingleDeviceView/Dashboard';
import Notifications from '../../containers/WeatherDisplay/SingleDeviceView/Notifications';
import SimulationView from '../../containers/WeatherDisplay/SingleDeviceView/SimulationView';
import EntriesMobile from '../../components/EntriesMobile';
import CommandForm from '../../components/Forms/Command';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';

const WeatherDisplayMobile = ({ detectValidToken }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Read selfData (may be null after logout)
  const rawSelfData = sessionStorage.getItem('selfData');
  const selfData = rawSelfData ? JSON.parse(rawSelfData) : null;

  // Safely derive fields (may be undefined for one render when logged out)
  const planLevel = selfData?.planLevel;
  const viewPermissions = selfData?.viewPermissions ?? '';
  const cmdPermissions = selfData?.cmdPermissions ?? [];
  const metricValOn = selfData?.metricValOn;

  const [selectedDevice, setSelectedDevice] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const { devices } = useDevices(isLoggingOut, setIsLoading, detectValidToken);

  const [, setEntryStartTimeIndex] = useState(null);
  const [, setEntryEndTimeIndex] = useState(null);
  const { deviceEntries } = useEntries(
    selectedDevice,
    setIsLoading,
    setEntryStartTimeIndex,
    setEntryEndTimeIndex,
    planLevel,
  );

  // normalize hash -> "table", "command", etc; default to table
  const rawHash = (location.hash || '').replace('#', '').toLowerCase();
  const hash = rawHash || VIEW_TABLE.toLowerCase();

  // If no hash, push #table ONCE (after first render, not in render)
  useEffect(() => {
    if (!location.hash) {
      navigate('/weather-display#table', { replace: true });
    }
  }, [location.hash, navigate]);

  useDeepEffect(() => {
    if (devices.length > 0 && __isEmpty(selectedDevice)) {
      setSelectedDevice(devices[0]);
    } else if (devices.length === 0 && !isLoading) {
      setIsLoading(true);
    }
  }, [devices, isLoading, selectedDevice]);

  // tabs scroll control
  useEffect(() => {
    const chooser = document.querySelector('.view-chooser');
    if (!chooser) return;

    if (hash === VIEW_NOTIFICATIONS.toLowerCase()) {
      chooser.scrollTo(chooser.clientWidth, 0);
    } else if (hash === VIEW_TABLE.toLowerCase()) {
      chooser.scrollTo(0, 0);
    }
  }, [hash]);

  const selectDevice = (e) => {
    e.preventDefault();
    const deviceObj = devices.find((device) => device.imei === e.target.value);
    setSelectedDevice(deviceObj || {});
  };

  const setPanelView = () => {
    if (isLoading || __isEmpty(selectedDevice)) {
      return <div className="loading-data">Loading Data...</div>;
    }

    switch (hash) {
      case VIEW_COMMAND.toLowerCase(): {
        const deviceInfoSlim = {
          id: selectedDevice.id,
          imei: selectedDevice.imei,
          name: selectedDevice.name,
          modem: selectedDevice.modem,
          type: selectedDevice.type,
        };
        return <CommandForm device={deviceInfoSlim} />;
      }

      case VIEW_DASHBOARD.toLowerCase():
        return <Dashboard device={selectedDevice} />;

      case VIEW_SIMULATION.toLowerCase():
        return <SimulationView />;

      case VIEW_NOTIFICATIONS.toLowerCase():
        return (
          <Notifications
            device={selectedDevice}
            showMetricOnly={metricValOn}
          />
        );

      case VIEW_TABLE.toLowerCase():
      default:
        // Default/unknown hash behaves like TABLE view (no navigate here)
        return (
          <EntriesMobile
            deviceType={selectedDevice.type}
            deviceTypeOfficial={`${selectedDevice.type} ${selectedDevice.model} (${selectedDevice.modem})`}
            deviceName={selectedDevice.name}
            deviceImei={selectedDevice.imei}
            entries={[...deviceEntries]}
            showMetricOnly={metricValOn}
          />
        );
    }
  };

  // If logged out (selfData cleared), redirect AFTER hooks are called
  if (!selfData) {
    return <Navigate to="/" replace />;
  }

  let views = [VIEW_TABLE, VIEW_DASHBOARD];
  if (planLevel !== ACCT_PLAN_LEVELS.BASIC) {
    views.push(VIEW_NOTIFICATIONS);
  }
  if (
    selectedDevice &&
    (viewPermissions === 'ALL' || cmdPermissions.includes(selectedDevice.imei))
  ) {
    views.splice(1, 0, VIEW_COMMAND);
  }
  // Add Simulation tab for testing (development only)
  if (process.env.NODE_ENV === 'development') {
    views.push(VIEW_SIMULATION);
  }

  return (
    <div className="weather-display-mobile">
      <HeaderDefault setIsLoggingOut={setIsLoggingOut} />
      <div className="device-selector">
        <select onChange={selectDevice} value={selectedDevice.imei || ''}>
          {devices.map((device, i) => (
            <option key={i} value={device.imei}>
              {device.type} - {device.name || device.imei}
            </option>
          ))}
        </select>
      </div>
      <ViewChooser type="entries" views={views} />
      {setPanelView()}
      <FooterMobile setIsLoggingOut={setIsLoggingOut} />
    </div>
  );
};

export default WeatherDisplayMobile;